/**
 * Session Guard — holistic session monitoring service.
 * 
 * Detects stale/expired sessions and forces logout regardless of which page the user is on.
 * Works alongside the existing axios 401 interceptor as a safety net for edge cases:
 * - Token removed from localStorage by another tab
 * - JWT expired while user is idle on a page with no API calls
 * - Auto-logout clears token but React state keeps the page rendered
 * 
 * Usage: Called once in App.js — covers the entire app.
 */

import { clearAllQuoteServiceDrafts } from '@/components/ndis/utils/quoteServiceDraft';

const SESSION_CHECK_INTERVAL = 30000; // 30 seconds
const LOGIN_PATH = '/login?session_expired=true';

let intervalId = null;
let logoutInProgress = false;

function isLoggedIn() {
  return !!localStorage.getItem('access_token');
}

function isOnPublicPage() {
  const publicPaths = ['/login', '/signup', '/', '/quote', '/applicant', '/referee', '/onboarding', '/r/goal/', '/book-demo', '/demo/tour', '/help', '/sign/', '/interview/', '/p/', '/legal/'];
  const path = window.location.pathname;
  return publicPaths.some(p => path === p || path.startsWith(p));
}

function forceLogout() {
  if (logoutInProgress) return;
  logoutInProgress = true;
  clearAllQuoteServiceDrafts();
  window.__sessionExpiredLogout = true;
  localStorage.removeItem('access_token');
  localStorage.removeItem('session_last_activity');
  localStorage.removeItem('session_expires_at');
  window.dispatchEvent(new Event('user-logout'));
  window.location.href = LOGIN_PATH;
}

function checkSession() {
  if (isOnPublicPage()) return;
  if (!isLoggedIn()) {
    forceLogout();
  }
}

// Decode JWT and check expiry without a library
function isTokenExpired() {
  const token = localStorage.getItem('access_token');
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    // Expired if current time > exp (with 30s buffer)
    return Date.now() / 1000 > payload.exp - 30;
  } catch {
    return true; // Malformed token = expired
  }
}

function checkTokenExpiry() {
  if (isOnPublicPage()) return;
  if (isTokenExpired()) {
    forceLogout();
  }
}

const sessionGuard = {
  start() {
    if (intervalId) return; // Already running

    // Periodic check
    intervalId = setInterval(() => {
      checkSession();
      checkTokenExpiry();
    }, SESSION_CHECK_INTERVAL);

    // Check on window focus (user returns to tab)
    window.addEventListener('focus', checkSession);
    window.addEventListener('focus', checkTokenExpiry);

    // Check on storage change (another tab logged out)
    window.addEventListener('storage', (e) => {
      if (e.key === 'access_token' && !e.newValue) {
        checkSession();
      }
    });

    // Listen for the custom logout event from App.js
    window.addEventListener('user-logout', () => {
      if (!isOnPublicPage()) {
        window.location.href = LOGIN_PATH;
      }
    });
  },

  stop() {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    window.removeEventListener('focus', checkSession);
    window.removeEventListener('focus', checkTokenExpiry);
  }
};

export default sessionGuard;
